import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { CopyButton } from "@arcadeai/design-system/components/ui/atoms/copy-button";
import { ArcadeLogo } from "@arcadeai/design-system/components/ui/atoms/icons/arcade-logo";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import {
  ErrorTemplate,
  ErrorTemplateActions,
  ErrorTemplateContent,
  ErrorTemplateDescription,
  ErrorTemplateIcon,
  ErrorTemplateLogo,
  ErrorTemplateTitle,
  ErrorTemplateUrl,
} from "@arcadeai/design-system/components/ui/templates/error-template";
import { captureException, withScope } from "@sentry/react";
import { Link, useLocation, useRouter } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowRight,
  Building2,
  Home,
  RefreshCw,
  SearchX,
} from "lucide-react";
import { type ReactNode, useEffect } from "react";
import { OrgProjectRouteError } from "./constants";

export function IncompleteUrlFallback() {
  return (
    <RouteRecovery
      description="The organization or project in this URL could not be found."
      icon={<SearchX className="h-10 w-10 text-muted-foreground/60" />}
      recoverTo="/"
      title="Not Found"
    />
  );
}

export function OrgProjectErrorFallback({ error }: { error: unknown }) {
  const router = useRouter();
  const isRouteError = error instanceof OrgProjectRouteError;
  const isEmpty = isRouteError && error.result.outcome === "empty";
  const fallbackContext = isRouteError ? error.result.fallbackContext : null;

  useEffect(() => {
    if (isRouteError) {
      return;
    }

    withScope((scope) => {
      scope.setTag("errorBoundary", "org-project-context");
      scope.setLevel("error");
      scope.setContext("route", {
        pathname: window.location.pathname,
        search: window.location.search,
        timestamp: new Date().toISOString(),
      });
      captureException(
        error instanceof Error
          ? error
          : new Error("Org/project context error (non-Error thrown)")
      );
    });
  }, [error, isRouteError]);

  let title = "Something Went Wrong";
  let description =
    "We could not validate your organization and project context. Please try again.";
  let icon: ReactNode = (
    <AlertTriangle className="h-10 w-10 text-muted-foreground/60" />
  );

  if (isRouteError) {
    if (isEmpty) {
      const hasOrgs = error.result.organizations.length > 0;
      title = hasOrgs ? "No Projects Available" : "No Organizations Available";
      description = hasOrgs
        ? "You don't have access to any projects yet. Contact your administrator to be added to a project."
        : "Your account does not have access to any organizations or projects.";
      icon = <Building2 className="h-10 w-10 text-muted-foreground/60" />;
    } else {
      title = "Not Found";
      description =
        "The organization or project in this URL could not be found or you do not have access.";
      icon = <SearchX className="h-10 w-10 text-muted-foreground/60" />;
    }
  }

  let recoverTo: string | undefined;
  let recoverParams: Record<string, string> | undefined;
  let recoverLabel = "Go Home";

  if (fallbackContext) {
    recoverLabel = "Go to Default";
    recoverTo = "/orgs/$organizationId/projects/$projectId/home";
    recoverParams = fallbackContext;
  } else if (isRouteError && !isEmpty) {
    recoverTo = "/";
  }

  return (
    <RouteRecovery
      description={description}
      icon={icon}
      onRetry={isRouteError ? undefined : () => router.invalidate()}
      recoverLabel={recoverLabel}
      recoverParams={recoverParams}
      recoverTo={recoverTo}
      showUrl={!isEmpty}
      title={title}
    />
  );
}

function RouteUrlValue({ currentPath }: { currentPath: string }) {
  return (
    <div className="mt-5 flex items-center justify-center gap-2">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger
            render={
              <div>
                <ErrorTemplateUrl className="mt-0" url={currentPath} />
              </div>
            }
          />
          <TooltipContent className="max-w-xl break-all" side="top">
            {currentPath}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger
            render={
              <CopyButton
                className="size-9 shrink-0"
                content={currentPath}
                variant="outline"
              />
            }
          />
          <TooltipContent side="top">Copy full URL</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

function RouteRecovery({
  title,
  description,
  icon,
  recoverLabel = "Go Home",
  recoverTo,
  recoverParams,
  retryLabel = "Retry",
  onRetry,
  showUrl = true,
}: {
  title: string;
  description: string;
  icon?: ReactNode;
  recoverLabel?: string;
  recoverTo?: string;
  recoverParams?: Record<string, string>;
  retryLabel?: string;
  onRetry?: () => void;
  showUrl?: boolean;
}) {
  const location = useLocation();
  const currentPath =
    typeof window === "undefined"
      ? location.pathname
      : `${window.location.pathname}${window.location.search}`;
  const hasActions = recoverTo != null || onRetry != null;

  return (
    <ErrorTemplate>
      <ErrorTemplateContent>
        <ErrorTemplateLogo>
          <ArcadeLogo className="h-7 w-auto text-foreground" />
        </ErrorTemplateLogo>

        {icon && <ErrorTemplateIcon>{icon}</ErrorTemplateIcon>}

        <ErrorTemplateTitle>{title}</ErrorTemplateTitle>
        <ErrorTemplateDescription>{description}</ErrorTemplateDescription>
        {showUrl && <RouteUrlValue currentPath={currentPath} />}

        {hasActions && (
          <ErrorTemplateActions>
            {recoverTo != null && (
              <Button
                className="h-12 min-w-[160px] px-10 font-semibold text-base shadow-lg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl"
                render={
                  <Link
                    // biome-ignore lint/suspicious/noExplicitAny: route params are dynamic at this level
                    // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
                    {...({ to: recoverTo, params: recoverParams } as any)}
                  />
                }
                size="lg"
              >
                <ArrowRight className="mr-2 h-4 w-4" />
                {recoverLabel}
              </Button>
            )}
            {onRetry ? (
              <Button
                className="h-11 min-w-[140px] px-8 text-base transition-transform hover:-translate-y-0.5"
                onClick={onRetry}
                size="lg"
                variant="outline"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                {retryLabel}
              </Button>
            ) : null}
            {!onRetry && recoverTo != null && (
              <Button
                className="h-11 min-w-[140px] px-8 text-base transition-transform hover:-translate-y-0.5"
                render={<Link to="/" />}
                size="lg"
                variant="outline"
              >
                <Home className="mr-2 h-4 w-4" />
                Go Home
              </Button>
            )}
          </ErrorTemplateActions>
        )}
      </ErrorTemplateContent>
    </ErrorTemplate>
  );
}
